import React, { useState } from "react";
import { Order } from "../../services/orderService";
import { adminOrderService } from "../../services/admin/orderService";
import OrderStatusUpdate from "./OrderStatusUpdate";

interface OrderDetailProps {
  order: Order;
  onClose: () => void;
  onStatusUpdate: () => void;
}

const OrderDetail: React.FC<OrderDetailProps> = ({
  order,
  onClose,
  onStatusUpdate,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "confirmed":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "preparing":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      case "shipped":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "canceled":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      new: "جديد",
      confirmed: "مؤكد",
      preparing: "قيد التحضير",
      shipped: "تم الشحن",
      canceled: "ملغي",
    };
    return labels[status] || status;
  };

  return (
    <div className="h-full bg-white dark:bg-slate-800 shadow-lg p-4 md:p-6 overflow-y-auto">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-[#0d171b] dark:text-white">
          تفاصيل الطلب
        </h2>
        <button
          onClick={onClose}
          className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl md:text-2xl w-8 h-8 flex items-center justify-center"
        >
          ✕
        </button>
      </div>

      <div className="space-y-3 md:space-y-4">
        <div>
          <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            رقم الطلب
          </label>
          <p className="text-sm md:text-base text-[#0d171b] dark:text-white font-semibold break-all">
            <span dir="ltr" style={{ display: 'inline-block', textAlign: 'left' }}>{order.orderNumber}</span>
          </p>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            الحالة
          </label>
          <span
            className={`inline-block px-2 py-1 rounded text-xs md:text-sm ${getStatusColor(
              order.status
            )}`}
          >
            {getStatusLabel(order.status)}
          </span>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            المنتج
          </label>
          <p className="text-sm md:text-base text-[#0d171b] dark:text-white break-words">{order.product.name}</p>
          {order.product.category && (
            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">
              الفئة: {order.product.category.name}
            </p>
          )}
        </div>

        {order.variant && (
          <div>
            <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              المتغير
            </label>
            <p className="text-sm md:text-base text-[#0d171b] dark:text-white break-words">
              {order.variant.variantType}: {order.variant.variantValue}
            </p>
          </div>
        )}

        <div>
          <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            السعر النهائي
          </label>
          <p className="text-base md:text-lg text-[#0d171b] dark:text-white font-semibold">
            {Number(order.finalPrice).toFixed(2)} د.ج
          </p>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            معلومات العميل
          </label>
          <div className="space-y-1">
            <p className="text-xs md:text-sm text-[#0d171b] dark:text-white break-words">
              الاسم: {order.customerName}
            </p>
            <p className="text-xs md:text-sm text-[#0d171b] dark:text-white break-all">
              الهاتف: <span dir="ltr" style={{ display: 'inline-block', textAlign: 'left' }}>{order.customerPhone}</span>
            </p>
            <p className="text-xs md:text-sm text-[#0d171b] dark:text-white break-words">
              الولاية: {order.wilaya}
            </p>
            <p className="text-xs md:text-sm text-[#0d171b] dark:text-white break-words">
              البلدية: {order.commune}
            </p>
          </div>
        </div>

        {order.notes && (
          <div>
            <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              ملاحظات
            </label>
            <p className="text-xs md:text-sm text-[#0d171b] dark:text-white break-words">{order.notes}</p>
          </div>
        )}

        <div>
          <label className="block text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            التواريخ
          </label>
          <div className="space-y-1 text-xs md:text-sm">
            <p className="text-slate-600 dark:text-slate-400 break-words">
              تاريخ الإنشاء:{" "}
              {new Date(order.createdAt).toLocaleString("ar-DZ")}
            </p>
            {order.statusUpdatedAt && (
              <p className="text-slate-600 dark:text-slate-400 break-words">
                آخر تحديث للحالة:{" "}
                {new Date(order.statusUpdatedAt).toLocaleString("ar-DZ")}
              </p>
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
          <OrderStatusUpdate order={order} onStatusUpdate={onStatusUpdate} />
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;

